import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { v4 as uuidv4 } from 'uuid';

import { AppDispatch, store } from 'app/store';

import {
  DossierSectionOptions,
  DossierSectionStatusOptions,
} from 'features/captains-form/slices/constants';
import {
  deleteCrewListFile,
  deleteCrewListPDF,
  Document,
  downloadCrewList,
  downloadCrewListTemplate,
  downloadDocument,
  downloadPreviousCrewList,
  selectDocuments,
  uploadCrewList,
  uploadDocument,
} from 'features/captains-form/slices/documentsSlice';
import {
  createPortcallStatusLog,
  FileMetadata,
  getCrew,
  getLastPortCallData,
  getPortcallStatusLog,
  selectCrew,
  selectLastPortCallData,
  selectPassengers,
  selectPortallStatusLog,
  State,
  StatusLog,
} from 'features/captains-form/slices/portCallSlice';
import { showPassengersSection } from 'features/layout/components/Sidebar';

import PersonListForm from './PersonListForm';
import {
  DOCUMENT_CATEGORIES,
  DOCUMENT_TYPE_IDS,
  DOCUMENT_TYPE_NAMES,
  DocumentCategory,
  DocumentSubCategory,
} from '../../documents/constants';

import '../styles/crew.scss';

type CrewProps = {
  isArrival?: boolean;
};

export const PDF_EXTENSION = '.pdf';
const MAX_FILE_SIZE = 10; //MB

const Crew = ({ isArrival = false }: CrewProps) => {
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pdfFileInputRef = useRef<HTMLInputElement>(null);
  const listType = isArrival ? 'arrival' : 'departure';

  const { arrival, departure } = useSelector((state: State) =>
    selectCrew(state)
  );
  const { arrival: arrivalPassengers, departure: departurePassengers } =
    useSelector(selectPassengers);
  const {
    portcallUid: lastPortCallUid,
    portName: lastPortCallPort,
    eta: lastPortCallEta,
  } = useSelector((state: State) => selectLastPortCallData(state));
  const portCallDocuments = useSelector(selectDocuments)?.documents;

  const [currentCrewFile, setCurrentCrewFile] = useState<
    FileMetadata | undefined
  >();
  const [arrivalCrewListToUpload, setArrivalCrewListToUpload] = useState<
    File | undefined
  >();
  const [departureCrewListToUpload, setDepartureCrewListToUpload] = useState<
    File | undefined
  >();

  const [currentCrewPDF, setCurrentCrewPDF] = useState<Document | undefined>();
  const [arrivalCrewPDFToUpload, setArrivalCrewPDFToUpload] = useState<
    File | undefined
  >();
  const [departureCrewPDFToUpload, setDepartureCrewPDFToUpload] = useState<
    File | undefined
  >();

  const [isNotificationDisplaying, setIsNotificationDisplaying] = useState<
    { message: string } | boolean
  >(false);
  const [notificationAlertType, setNotificationAlertType] = useState<
    'success' | 'error'
  >();
  const [notificationTitle, setNotificationTitle] = useState<string>();

  const [fileToDelete, setFileToDelete] = useState<'excel/csv' | 'pdf'>();
  const [isDeleteFileModalOpen, setIsDeleteFileModalOpen] = useState(false);

  const portCallStatusLog: StatusLog[] = useSelector(selectPortallStatusLog);

  const nextPath = useMemo(() => {
    const isPassenger = showPassengersSection(
      arrivalPassengers?.passengerQuantity,
      departurePassengers?.passengerQuantity
    );
    let next = 'marpol/confirm-waste';
    if (isPassenger) {
      next = 'arrival-passengers';
    }
    return `/${params.uid}/${isArrival ? 'departure-crew' : next}`;
  }, [
    arrivalPassengers?.passengerQuantity,
    departurePassengers?.passengerQuantity,
    isArrival,
    params.uid,
  ]);

  const isCrewListSubmmitedToNMap = useMemo(() => {
    const section = isArrival
      ? DossierSectionOptions.ArrivalCrew
      : DossierSectionOptions.DepartureCrew;
    if (portCallStatusLog && portCallStatusLog?.length > 0) {
      const statusLog = portCallStatusLog?.find(
        (log) => log?.section === section
      );
      return (
        statusLog &&
        statusLog?.status === DossierSectionStatusOptions.SubmittedToNmap
      );
    }
    return false;
  }, [isArrival, portCallStatusLog]);

  useEffect(() => {
    if (params.uid) {
      dispatch(getCrew({ uid: params.uid, listType }));
      dispatch(getLastPortCallData({ uid: params.uid, list: listType }));
    }
  }, [dispatch, isArrival, listType, params.uid]);

  useEffect(() => {
    if (isArrival) {
      setCurrentCrewFile(arrival?.fileMetadata);
    } else {
      setCurrentCrewFile(departure?.fileMetadata);
    }
  }, [arrival?.fileMetadata, departure?.fileMetadata, isArrival]);

  useEffect(() => {
    const cat = isArrival
      ? DOCUMENT_CATEGORIES.PRE_ARRIVAL_DOCUMENTS
      : DOCUMENT_CATEGORIES.PRE_DEPARTURE_DOCUMENTS;
    const docTypeId = isArrival
      ? DOCUMENT_TYPE_IDS.UPLOAD_ARRIVAL_CREW
      : DOCUMENT_TYPE_IDS.UPLOAD_DEPARTURE_CREW;
    const categoryDocuments =
      portCallDocuments[cat as keyof typeof portCallDocuments];
    const currentDocuments = categoryDocuments?.filter((doc) => {
      return doc?.documentType.id === docTypeId;
    });
    if (isArrival) {
      setCurrentCrewPDF(currentDocuments?.[0]);
    } else {
      setCurrentCrewPDF(currentDocuments?.[0]);
    }
  }, [
    isArrival,
    portCallDocuments,
    portCallDocuments.preArrivalDocuments,
    portCallDocuments.preDepartureDocuments,
  ]);

  const crewListToUpload = useMemo(() => {
    return isArrival ? arrivalCrewListToUpload : departureCrewListToUpload;
  }, [arrivalCrewListToUpload, departureCrewListToUpload, isArrival]);

  const crewPDFToUpload = useMemo(() => {
    return isArrival ? arrivalCrewPDFToUpload : departureCrewPDFToUpload;
  }, [arrivalCrewPDFToUpload, departureCrewPDFToUpload, isArrival]);

  const currentCrewListPreview = useMemo(() => {
    if (currentCrewFile) {
      return {
        filename: currentCrewFile.filename,
        lastUpdated: currentCrewFile.uploadDate,
      };
    } else if (crewListToUpload) {
      return {
        filename: crewListToUpload?.name,
        lastUpdated: new Date().toString(),
      };
    } else {
      return;
    }
  }, [crewListToUpload, currentCrewFile]);

  const currentCrewPDFPreview = useMemo(() => {
    if (currentCrewPDF) {
      return {
        filename: currentCrewPDF.documentType.name,
        lastUpdated: currentCrewPDF.lastUpdated,
      };
    } else if (crewPDFToUpload) {
      return {
        filename: crewPDFToUpload?.name,
        lastUpdated: new Date().toString(),
      };
    } else {
      return;
    }
  }, [crewPDFToUpload, currentCrewPDF]);

  const downloadLocalFile = (file: File) => {
    const url = window.URL.createObjectURL(file);
    const link = document.createElement('a');

    link.href = url;
    link.download = file.name;
    link.style.display = 'none';

    document.body.appendChild(link);
    link.click();

    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 100);
  };

  const updateStatusLog = async () => {
    if (params.uid) {
      const isSuccessUpdateStatus = await dispatch(
        createPortcallStatusLog({
          statusLog: {
            uid: params.uid,
            section: isArrival
              ? DossierSectionOptions.ArrivalCrew
              : DossierSectionOptions.DepartureCrew,
            status: DossierSectionStatusOptions.SubmittedToNmap,
          },
        })
      ).then((response) => {
        if (response.type && response.type.endsWith('/rejected')) {
          return false;
        } else {
          return true;
        }
      });
      if (!isSuccessUpdateStatus) return;
      dispatch(getPortcallStatusLog(params.uid));
    }
  };

  const uploadCrewListFile = async (file: File) => {
    if (params.uid) {
      try {
        await dispatch(
          uploadCrewList({
            portcallUid: params.uid,
            file,
            listType,
          })
        ).unwrap();

        dispatch(
          getCrew({
            uid: params.uid,
            listType,
          })
        );
      } catch {
        alertNotification('error', 'Error', 'Upload failed. Please try again.');
      }
    }
  };

  const uploadCrewPDFFile = async (file: File) => {
    if (params.uid) {
      try {
        return dispatch(
          uploadDocument({
            documentUid: uuidv4(),
            portcallId: params.uid,
            category: isArrival
              ? DOCUMENT_CATEGORIES.PRE_ARRIVAL_DOCUMENTS
              : DOCUMENT_CATEGORIES.PRE_DEPARTURE_DOCUMENTS,
            documentCategory: DocumentCategory.DUE_DOCUMENTS,
            documentSubCategory: DocumentSubCategory.Captain,
            documentTypeId: isArrival
              ? DOCUMENT_TYPE_IDS.UPLOAD_ARRIVAL_CREW
              : DOCUMENT_TYPE_IDS.UPLOAD_DEPARTURE_CREW,
            documentTypeName: DOCUMENT_TYPE_NAMES.CREW_PDF_DOCUMENT,
            file: file,
            isCrewFile: true,
          })
        );
      } catch (error) {
        console.error('Upload failed:', error);
        alertNotification('error', 'Error', 'Upload failed. Please try again.');
      }
    }
  };

  const onSubmit = async () => {
    try {
      if (crewListToUpload) {
        await uploadCrewListFile(crewListToUpload);

        let pdfUploadResult;
        if (crewPDFToUpload) {
          pdfUploadResult = await uploadCrewPDFFile(crewPDFToUpload);
        }

        const isListUploaded =
          store.getState().documents.uploadCrewListStatus === 'succeeded';
        const isPDFUploaded =
          !crewPDFToUpload ||
          pdfUploadResult?.meta.requestStatus === 'fulfilled';

        if (isListUploaded && isPDFUploaded) {
          await updateStatusLog();
          navigate(nextPath);
        }
      } else if (currentCrewFile) {
        let pdfUploadResult;
        if (crewPDFToUpload) {
          pdfUploadResult = await uploadCrewPDFFile(crewPDFToUpload);
        }

        const isPDFUploaded =
          !crewPDFToUpload ||
          pdfUploadResult?.meta.requestStatus === 'fulfilled';

        if (isPDFUploaded) {
          await updateStatusLog();
          navigate(nextPath);
        }
      }
    } catch (error) {
      console.error('Upload failed:', error);
    }
  };

  const handleDownloadTemplate = () => {
    params.uid &&
      dispatch(
        downloadCrewListTemplate({
          uid: params.uid,
          listType,
        })
      );
  };

  const handleDownloadCrewList = () => {
    if (currentCrewFile) {
      params.uid &&
        dispatch(
          downloadCrewList({
            uid: params.uid,
            listType,
            filename: currentCrewFile?.filename ?? 'crew-list.xlsx',
          })
        );
    } else {
      crewListToUpload && downloadLocalFile(crewListToUpload);
    }
  };

  const handleDownloadCrewPDF = () => {
    if (currentCrewPDF) {
      params.uid &&
        dispatch(
          downloadDocument({
            uiid: currentCrewPDF.documentUid,
            fileName: currentCrewPDF.documentType.name,
          })
        );
    } else {
      crewPDFToUpload && downloadLocalFile(crewPDFToUpload);
    }
  };

  const handleDeleteCrewList = () => {
    if (fileToDelete === 'excel/csv') {
      handleDeleteCrewListFile();
    } else if (fileToDelete === 'pdf') {
      handleDeleteCrewPDF();
    }
  };

  const handleDeleteCrewListFile = () => {
    if (currentCrewFile) {
      params.uid &&
        dispatch(
          deleteCrewListFile({
            uid: params.uid,
            listType,
          })
        ).then(() => {
          const isListDeleted =
            store.getState().documents.deleteCrewListFileStatus === 'succeeded';
          if (isListDeleted) {
            setIsDeleteFileModalOpen(false);
            setCurrentCrewFile(undefined);
            isArrival
              ? setArrivalCrewListToUpload(undefined)
              : setDepartureCrewListToUpload(undefined);
            alertNotification(
              'success',
              'Success',
              'Crew list file deleted successfully!'
            );
          } else {
            alertNotification(
              'error',
              'Error',
              'An error occurred, please try again!'
            );
          }
        });
    } else {
      setIsDeleteFileModalOpen(false);
      setCurrentCrewFile(undefined);
      isArrival
        ? setArrivalCrewListToUpload(undefined)
        : setDepartureCrewListToUpload(undefined);
    }
  };

  const handleDeleteCrewPDF = () => {
    if (currentCrewPDF) {
      params.uid &&
        dispatch(
          deleteCrewListPDF({
            uid: params.uid,
            documentUid: currentCrewPDF?.documentUid,
          })
        ).then(() => {
          const isPDFDeleted =
            store.getState().documents.deleteCrewListPDFStatus === 'succeeded';
          if (isPDFDeleted) {
            setIsDeleteFileModalOpen(false);
            setCurrentCrewPDF(undefined);
            isArrival
              ? setArrivalCrewPDFToUpload(undefined)
              : setDepartureCrewPDFToUpload(undefined);
            alertNotification(
              'success',
              'Success',
              'Crew list file deleted successfully!'
            );
          } else {
            alertNotification(
              'error',
              'Error',
              'An error occurred, please try again!'
            );
          }
        });
    } else {
      setIsDeleteFileModalOpen(false);
      setCurrentCrewPDF(undefined);
      isArrival
        ? setArrivalCrewPDFToUpload(undefined)
        : setDepartureCrewPDFToUpload(undefined);
    }
  };

  const alertNotification = (
    type: 'success' | 'error',
    title: string,
    message: string
  ) => {
    setIsNotificationDisplaying({ message: message });
    setNotificationAlertType(type);
    setNotificationTitle(title);
  };

  const handleUploadCrewList = () => {
    fileInputRef.current?.click();
  };

  const getPreviousCrewList = () => {
    if (lastPortCallUid) {
      dispatch(
        downloadPreviousCrewList({ uid: lastPortCallUid, listType })
      ).then((_) => {
        if (_.meta && _.meta.requestStatus === 'rejected') {
          alertNotification(
            'error',
            'Error',
            'An error occurred while fetching the previous crew list.'
          );
          return;
        } else {
          const { contentType, data, fileName } = _.payload as {
            contentType: string;
            data: string;
            fileName: string;
          };
          const file = new File(
            [data as string | ArrayBuffer | Uint8Array],
            fileName,
            {
              type: contentType,
            }
          );
          if (isArrival) {
            setArrivalCrewListToUpload(file);
          } else {
            setDepartureCrewListToUpload(file);
          }
          alertNotification(
            'success',
            'Success',
            'Previous crew list uploaded successfully!'
          );
        }
      });
    }
  };

  const handleUploadCrewListPDF = () => {
    pdfFileInputRef.current?.click();
  };

  const isValidFile = (file: File, extensions: string) => {
    const fileExtension = file?.name.split('.').pop()?.toLowerCase();

    const isInvalidType = !extensions.includes(fileExtension ?? '');
    if (isInvalidType) {
      alertNotification(
        'error',
        'Error',
        `Unsupported file type. Please upload a file with supported type (${extensions}).`
      );
      return false;
    }

    const maxSizeBytes = MAX_FILE_SIZE * 1024 * 1024;
    const isFileSizeValid = file.size <= maxSizeBytes;
    if (!isFileSizeValid) {
      alertNotification(
        'error',
        'Error',
        `File is too large. Maximum allowed size is ${MAX_FILE_SIZE}MB.`
      );
      return false;
    }
    return true;
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    extensions: string
  ) => {
    const file = event.target.files?.[0];
    if (file && params.uid) {
      if (isValidFile(file, extensions)) {
        if (isArrival) {
          setArrivalCrewListToUpload(file);
        } else {
          setDepartureCrewListToUpload(file);
        }
      }
    }
  };

  const handlePDFFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    extensions: string
  ) => {
    const file = event.target.files?.[0];
    if (file && params.uid) {
      if (isValidFile(file, extensions)) {
        if (isArrival) {
          setArrivalCrewPDFToUpload(file);
        } else {
          setDepartureCrewPDFToUpload(file);
        }
      }
    }
  };

  return (
    <PersonListForm
      type={'crew'}
      title={isArrival ? 'Crew list on arrival' : 'Crew list on departure'}
      nextPath={nextPath}
      backPath={`/${params.uid}/${isArrival ? 'solas-and-isps-code/security-measures' : 'arrival-crew'}`}
      stepsDisabled={!currentCrewListPreview || isCrewListSubmmitedToNMap}
      isPersonListSubmmitedToNMap={isCrewListSubmmitedToNMap}
      submitDisabled={!currentCrewListPreview || isCrewListSubmmitedToNMap}
      onSubmit={onSubmit}
      currentPersonListPreview={currentCrewListPreview}
      currentPersonPDFPreview={currentCrewPDFPreview}
      handleDownloadTemplate={handleDownloadTemplate}
      handleDownloadPersonList={handleDownloadCrewList}
      handleDownloadPersonPDF={handleDownloadCrewPDF}
      handlePDFFileChange={handlePDFFileChange}
      handleFileChange={handleFileChange}
      handleUploadPersonList={handleUploadCrewList}
      handleUploadPersonListPDF={handleUploadCrewListPDF}
      fileInputRef={fileInputRef}
      pdfFileInputRef={pdfFileInputRef}
      isDeleteFileModalOpen={isDeleteFileModalOpen}
      setIsDeleteFileModalOpen={setIsDeleteFileModalOpen}
      handleDeletePersonList={handleDeleteCrewList}
      setFileToDelete={setFileToDelete}
      isNotificationDisplaying={isNotificationDisplaying}
      setIsNotificationDisplaying={setIsNotificationDisplaying}
      notificationAlertType={notificationAlertType}
      notificationTitle={notificationTitle}
      isPreviousOptionDisplayed
      getPreviousCrewList={getPreviousCrewList}
      lastPortCallUid={lastPortCallUid}
      lastPortCallPort={lastPortCallPort}
      lastPortCallEta={lastPortCallEta}
    />
  );
};

export default Crew;
